/**
 * Die Gliederung des Gewerks MSR/Gebäudeautomation — an EINER Stelle.
 * Muster N1 (Kopfkommentar `seiten/heizlastkuehllast/abschnitte.ts`),
 * umgesetzt in Paket N2.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM ELF UNTERPUNKTE UND NICHT DIE VIER AUS DEM PLANENTWURF
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Plan Abschnitt 3.3 schlug „Leistungsbilanz", „ASP-Kapazität" und
 * „Effizienzklasse" als eigene BERECHNUNG-Unterpunkte vor, abgeleitet aus
 * den Tabellen `msr_leistungsbilanz`/`msr_asp_kapazitaet`/
 * `msr_effizienzklasse`. Gemessen an den tatsächlichen elf Reitern
 * (`msr/Reiter.tsx`, jetzt aufgelöst) sind das KEINE eigenen Seiten,
 * sondern Spalten/Felder INNERHALB der ASP-Liste und der ASP-Detailseite
 * (`msr/Asps.tsx`, `msr/AspDetail.tsx`) bzw. innerhalb der Festlegungen
 * (`msr/Festlegungen.tsx`). Navigation verweist nur auf real Vorhandenes -
 * deshalb wird „Schaltschränke / ASPs" der Berechnungs-Eintrag, der
 * Leistungsbilanz und ASP-Kapazität sichtbar macht, statt drei erfundener
 * Unterpunkte ohne eigene Route.
 *
 * Ebenso „Befunde" (`msr_befunde`, Plan: prüfung): Es gibt KEINE eigene
 * Befundeliste als Seite — `msr/Befunde.tsx` ist eine Teilkomponente, die
 * NUR eingebettet in `AnlageDetail`/`AspDetail` erscheint (Befunde EINES
 * Objekts), keine eigenständig navigierbare Übersicht. Sie bleibt deshalb
 * ohne eigenen Menüpunkt (Plan Abschnitt 6).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE VIER STANDARD-ZWISCHENZEILEN, GEMESSEN VERTEILT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Planung: Baum (die Anlagen/ASP-Struktur, aus der alles andere folgt) und
 * Grundlagen (Regelwerk/VDI 3814, Basis für die Ableitung — nicht deren
 * Ergebnis).
 *
 * Berechnungen: Schaltschränke/ASPs (trägt Leistungsbilanz und
 * ASP-Kapazität als Spalten, s. o.) und AKS (der Ableitungslauf vergibt
 * hier AKS-Kennungen — Text im Bestätigungsdialog: „Vergibt
 * AKS-Kennungen").
 *
 * Daten: Datenpunktliste, GA-Funktionen, Raumautomation, M-Bus, Schemen,
 * Dokumente — sechs reine Bestandslisten/Ablagen.
 *
 * Prüfung: Übersicht — sie zeigt den letzten Ableitungslauf mit seinen
 * Gegenproben (`StatusBadge` „in Ordnung"/„Prüfen"/„Fehler"), also
 * Prüfergebnisse und keine eigene Fachfrage. Sie ist zugleich der
 * Index-Eintrag (`pfad: 'msr'`) und fällt in `alleEintraege` automatisch
 * heraus — GEMESSEN zeigt die Seitenleiste deshalb nur drei Zwischenzeilen
 * (Planung, Berechnungen, Daten), „Prüfung" bleibt ohne sichtbare Zeile,
 * weil ihr einziger Eintrag der gefilterte Index ist. Dasselbe Verhalten
 * wie bei „Heizlast" unter Heiz-/Kühllast, nur dass dort die Zwischenzeile
 * noch andere Einträge trug und deshalb sichtbar blieb.
 */

export interface MsrEintrag {
  /** Der Pfad UNTER /projekte/:id/msr/ (bzw. 'msr' selbst fuer den Index). */
  pfad: string;
  name: string;
  /** Wozu die Seite da ist. Erscheint als Titel am Baumeintrag. */
  hinweis: string;
}

export interface MsrAbschnitt {
  name: string;
  eintraege: MsrEintrag[];
}

export const MSR_ABSCHNITTE: MsrAbschnitt[] = [
  {
    name: 'Planung',
    eintraege: [
      {
        pfad: 'msr/baum',
        name: 'Baum',
        hinweis: 'Die Anlagen- und ASP-Struktur, wahlweise nach Gewerk '
          + 'oder nach ASP gegliedert',
      },
      {
        pfad: 'msr/grundlagen',
        name: 'Grundlagen',
        hinweis: 'Festlegungen und Regelwerk (VDI 3814/VDI 3813) — die '
          + 'Basis, aus der die Ableitung folgt',
      },
      {
        // TM-8b: Annahmen und Vorgaben (TX-STANDARD S1.6) - dieselbe Liste wie die Blätter „Annahmen“ der Mappen.
        pfad: 'msr/annahmen',
        name: 'Annahmen und Vorgaben',
        hinweis: 'Genutzte Vorgaben und Annahmen mit Herkunft, Quelle und Bestätigung',
      },
    ],
  },
  {
    name: 'Berechnungen',
    eintraege: [
      {
        pfad: 'msr/asps',
        name: 'Schaltschränke / ASPs',
        hinweis: 'Leistungsbilanz und ASP-Kapazität je Schaltschrank',
      },
      {
        pfad: 'msr/aks',
        name: 'AKS',
        hinweis: 'Der AKS-Aufbau, den der Ableitungslauf vergibt — '
          + 'vergebene Kennungen werden nie geändert',
      },
    ],
  },
  {
    name: 'Daten',
    eintraege: [
      {
        pfad: 'msr/datenpunkte',
        name: 'Datenpunktliste',
        hinweis: 'Alle Datenpunkte, aus den Gewerken abgeleitet',
      },
      {
        pfad: 'msr/funktionen',
        name: 'GA-Funktionen',
        hinweis: 'Die Gebäudeautomationsfunktionen je Anlage',
      },
      {
        pfad: 'msr/raumautomation',
        name: 'Raumautomation',
        hinweis: 'Die Raumautomation je Raum',
      },
      {
        pfad: 'msr/mbus',
        name: 'M-Bus',
        hinweis: 'Die M-Bus-Zähler und ihre Anbindung',
      },
      {
        pfad: 'msr/schemen',
        name: 'Schemen',
        hinweis: 'Die abgelegten Anlagenschemen',
      },
      {
        pfad: 'msr/dokumente',
        name: 'Dokumente',
        hinweis: 'Erzeugte Dokumente zu Anlagen und ASPs',
      },
    ],
  },
  {
    name: 'Prüfung',
    eintraege: [
      {
        // Index-Route — pfad ist absichtlich der Fachbereichspfad selbst,
        // siehe Kopfkommentar Punkt 3 in heizlastkuehllast/abschnitte.ts.
        pfad: 'msr',
        name: 'Übersicht',
        hinweis: 'Kennzahlen, letzter Ableitungslauf mit Gegenproben',
      },
      {
        // TM-8a: die projektweite Befundliste nach Schwere (TX-STANDARD S1.5).
        pfad: 'msr/befunde',
        name: 'Befunde',
        hinweis: 'Befunde des Ableitungslaufs und die beim Abruf gebildeten, nach Schwere',
      },
    ],
  },
];
